import React from "react";
import { Box, Typography } from "@mui/material";
import { deepmerge } from "@mui/utils";
import { useTranslations } from "next-intl";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import { BasicInfoCta } from "@/types/dto/common/basic-info";
import { dynamicCtaButtonFactory } from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import type { MediaTextCardTextComponentProps } from "./media-text-card";

const externalUrlPattern = /^(?:https?:)?\/\//i;

const addExternalLinkAttributes = (html: string) =>
  html.replace(/<a\b([^>]*)>/gi, (openingTag, attributes: string) => {
    const hrefMatch = attributes.match(/\bhref\s*=\s*(['"])(.*?)\1/i);

    if (!hrefMatch || !externalUrlPattern.test(hrefMatch[2])) {
      return openingTag;
    }

    const attributesWithoutTargetOrRel = attributes
      .replace(/\s+target\s*=\s*(['"])[^'"]*\1/gi, "")
      .replace(/\s+rel\s*=\s*(['"])[^'"]*\1/gi, "");

    return `<a${attributesWithoutTargetOrRel} target="_blank" rel="noopener noreferrer">`;
  });

const DefaultText: React.FC<MediaTextCardTextComponentProps> = ({
  headerText,
  copyText,
  anchorTextColor,
  slotProps,
  testId,
  ctas = []
}) => {
  const t = useTranslations("COMMON");
  const dynamicCtas = ctas
    .filter(cta => Boolean(cta.callToActionText))
    .map(cta => dynamicCtaButtonFactory(cta as BasicInfoCta, t));

  return (
    <>
    {headerText && (
      <Typography
        variant="h2"
        data-testid={`media-text-card-header-${testId}`}
        sx={deepmerge(
          {
            color: "primary.main",
            fontSize: { xs: "2.4rem", sm: "2.6rem", md: "2.8rem" },
            position: "relative",
            display: "inline-block"
          },
          {
            ...slotProps?.header?.sx
          }
        )}
      >
        {headerText}
      </Typography>
    )}

    {copyText && (
      <Box
        sx={deepmerge(
          {
            color: "text.primary",
            fontSize: "1.8rem",
            lineHeight: "2.7rem",
            whiteSpace: "pre-line",
            width: "100%",
            fontWeight: 500,
            "& p": { margin: 0 },
            "& p + p": { marginTop: "1.6rem" },
            "& a": { color: anchorTextColor, textDecoration: "underline !important" }
          },
          {
            ...slotProps?.copy?.sx
          }
        )}
        data-testid={`media-text-card-copy-${testId}`}
        dangerouslySetInnerHTML={{ __html: addExternalLinkAttributes(copyText) }}
      />
    )}
    {dynamicCtas.map((dynamicCta, index) => {
      const ctaKey = `${testId}-${dynamicCta.ctaType}-${dynamicCta.label}-${JSON.stringify(dynamicCta.ctaData)}`;

      return (
        <DynamicCtaButton
          key={ctaKey}
          label={dynamicCta.label}
          ctaType={dynamicCta.ctaType}
          ctaData={dynamicCta.ctaData}
          testId={`media-text-card-cta-${testId}-${index}`}
          variant={dynamicCta.variant ?? "contained"}
          color={dynamicCta.color ?? "primary"}
        />
      );
    })}
    </>
  );
};

export default DefaultText;
